import { useCallback, useEffect, useRef } from "react";
import { withNamespaces } from "react-i18next";
import { Button } from "reactstrap";
import AssessmentActions, {
    submitQuestionnaireForm
} from "./AssessmentActions";
import AssessmentSaveButton from "./AssessmentSaveButton";

const getDefaultPageIndicator = (page, t) => ({
    label:
        page.progress.percent >= 100
            ? t("Completed")
            : t("In Progress"),
    variant: page.progress.percent >= 100 ? "completed" : "in-progress"
});

/**
 * Shared split presentation for assessment questionnaires.
 *
 * Product-specific labels and indicators are supplied through
 * settings.pagesLayoutContext. Every page remains mounted so form fields and
 * answers stay registered while another page is visible.
 */
const AssessmentPagesLayout = ({
    pages,
    activePageId,
    onPageChange,
    settings,
    t
}) => {
    const {
        navigationTitle = "Sections",
        onProgressChange,
        getPageIndicator,
        isSaving = false,
        canSave = true
    } = settings?.pagesLayoutContext || {};
    const contentRef = useRef();
    const activePage =
        pages.find((page) => page.id === activePageId) || pages[0];
    const activeIndex = activePage
        ? pages.findIndex((page) => page.id === activePage.id)
        : -1;

    const answered = pages.reduce(
        (sum, page) => sum + page.progress.answered,
        0
    );
    const total = pages.reduce(
        (sum, page) => sum + page.progress.total,
        0
    );
    const overallPercent = total
        ? Math.round((answered / total) * 100)
        : 0;

    const resolveIndicator = useCallback(
        (page) => {
            return (
                getPageIndicator?.(page, t) ||
                getDefaultPageIndicator(page, t)
            );
        },
        [getPageIndicator, t]
    );

    const goToPage = useCallback(
        (pageId) => {
            onPageChange(pageId);

            contentRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
        },
        [onPageChange]
    );

    const save = useCallback(() => {
        submitQuestionnaireForm(contentRef.current);
    }, []);

    useEffect(() => {
        onProgressChange?.(overallPercent);
    }, [onProgressChange, overallPercent]);

    if (!activePage) {
        return null;
    }

    const activeIndicator = resolveIndicator(activePage);

    return (
        <>
        <div className="assessment-panel__workspace smartintegrity__questionnaire__tabs_progress">
            <aside className="assessment-panel__sidebar">
                <div className="assessment-panel__sidebar-heading">
                    <strong>{t(navigationTitle)}</strong>
                </div>

                <div
                    className="assessment-panel__list"
                    role="tablist"
                    aria-label={t(navigationTitle)}
                >
                    {pages.map((page) => {
                        const indicator = resolveIndicator(page);
                        const hasErrors = page.validation?.hasErrors;

                        return (
                            <div
                                key={page.id}
                                data-tab-id={page.id}
                                role="tab"
                                tabIndex={0}
                                aria-selected={page.isActive}
                                aria-invalid={hasErrors || undefined}
                                className={`assessment-panel__item ${
                                    page.isActive ? "active" : ""
                                } ${hasErrors ? "has-validation-errors" : ""}`.trim()}
                                onClick={() => goToPage(page.id)}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                    ) {
                                        event.preventDefault();
                                        goToPage(page.id);
                                    }
                                }}
                            >
                                <span className="assessment-panel__number">
                                    {page.index + 1}
                                </span>
                                <span className="assessment-panel__item-main">
                                    <strong>{t(page.title)}</strong>
                                    <small>
                                        {`${page.progress.percent}% · ${page.progress.answered}/${page.progress.total} ${t("answered")}`}
                                    </small>
                                </span>
                                <span className="assessment-panel__item-meta">
                                    {hasErrors && (
                                        <span
                                            className="assessment-panel__validation-error"
                                            title={t("This section contains validation errors")}
                                        >
                                            <i
                                                className="ri-error-warning-line"
                                                aria-hidden="true"
                                            />
                                            <span aria-hidden="true">
                                                {page.validation.errorCount}
                                            </span>
                                        </span>
                                    )}
                                    <span
                                        className={`assessment-panel__status ${
                                            indicator.variant || ""
                                        }`}
                                    >
                                        {indicator.label}
                                    </span>
                                </span>
                                <i
                                    className="ri-arrow-right-s-line text-muted"
                                    aria-hidden="true"
                                />
                            </div>
                        );
                    })}
                </div>
            </aside>

            <main className="assessment-panel__content" ref={contentRef}>
                <div className="assessment-panel__questionnaire">
                    <div
                        className={`assessment-panel__content-heading ${
                            activePage.validation?.hasErrors
                                ? "has-validation-errors"
                                : ""
                        }`}
                    >
                        <span className="assessment-panel__number">
                            {activePage.index + 1}
                        </span>
                        <strong>{t(activePage.title)}</strong>
                        <span
                            className={`assessment-panel__status ms-auto ${
                                activeIndicator.variant || ""
                            }`}
                        >
                            {activeIndicator.label}
                        </span>
                    </div>

                    {pages.map((page) => (
                        <div
                            key={page.id}
                            role="tabpanel"
                            aria-hidden={!page.isActive}
                            data-assessment-page-id={page.id}
                            className={`assessment-panel__page questions-container ${
                                page.isActive ? "active" : ""
                            }`}
                        >
                            {page.render()}
                        </div>
                    ))}
                </div>
            </main>
        </div>

        <AssessmentActions>
            {activeIndex > 0 && (
                <Button
                    type="button"
                    color="light"
                    outline
                    onClick={() =>
                        goToPage(pages[activeIndex - 1].id)
                    }
                >
                    <i className="ri-arrow-left-s-line me-1" />
                    {t("Back")}
                </Button>
            )}

            {activeIndex < pages.length - 1 ? (
                <Button
                    type="button"
                    color="primary"
                    className="ms-auto"
                    onClick={() =>
                        goToPage(pages[activeIndex + 1].id)
                    }
                >
                    {t("Next")}
                    <i className="ri-arrow-right-s-line ms-1" />
                </Button>
            ) : (
                <AssessmentSaveButton
                    isSaving={isSaving}
                    disabled={!canSave}
                    onClick={save}
                />
            )}
        </AssessmentActions>
        </>
    );
};

export default withNamespaces()(AssessmentPagesLayout);
